<template>
  <div class="landing">
    <div class="landing-header">
      <img
        class="logo"
        src="/static/assets/branding-2022/img/sharedo-logo-horiz-250.png"
      />
      <div>
        <feature
          icon="fa-file-contract"
          text="Dynamic Document Templates"
        ></feature>
        <feature icon="fa-file-code" text="Re-usable Content"></feature>
        <feature
          icon="fa-code-branch"
          text="Document Lifecycle Management"
        ></feature>
      </div>
    </div>
    <div class="landing-body">
      <div class="content">
        <div v-if="isLegacyBrowser">
          <ie-warning />
        </div>
        <div v-else>
          <connect v-if="!connected"></connect>
          <login v-if="connected && !loggedIn" @login="login"></login>
        </div>
      </div>
      <div class="legal">
        <a target="_blank" :href="appSettings.privacyUrl">Privacy Policy</a>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import "@/_css/variables.scss";
.landing {
  height: 100%;

  & > .landing-header {
    height: 150px;
    background-color: $gray-800;
    color: $text-inverted;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    & > img.logo {
      margin-top: 10px;
      max-width: 250px;
      margin-bottom: 5px;
    }
  }

  & > .landing-body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    margin-top: -150px;
    padding: 165px 15px 15px 15px;

    & > .content {
      flex-grow: 1;
    }

    & > .legal {
      font-size: 0.8rem;
    }
  }
}
</style>
<script>
import { mapState } from "vuex";
import Feature from "@/components/landing/Feature.vue";
import Connect from "@/components/landing/Connect.vue";
import Login from "@/components/landing/Login.vue";
import IeWarning from "@/components/support/IEWarning.vue";
import tokenStorage from "@/services/auth/tokenStorageService";
import auth from "@/services/auth/authService";
import baseUrl from "@/services/common/baseUrlService";
import sharedoSettings from "@/services/sharedo/sharedoSettingsService";
import sharedoCapabilities from "@/services/sharedo/sharedoCapabilitiesService";
import sharedoDiscovery from "@/services/sharedo/sharedoDiscoveryService";
import encryption from "@/services/crypto/encryptionService";
import global from "@/global";
import { isLegacyBrowser } from "@/util/legacy";

const initialize = async function () {
  if (!this.connected && !!sharedoSettings.get()) {
    this.$store.commit("connected");
  }

  if (!!sharedoSettings.get()) {
    const caps = sharedoCapabilities.get();
    if (Object.keys(caps).length === 0) {
      try {
        const capabilities = await sharedoDiscovery.getCapabilities();
        sharedoCapabilities.set(capabilities);
      } catch (e) {
        this.logError("Failed to fetch capabilities in initialize", e);
      }
    }
  }

  if (this.connected) {
    const settings = sharedoSettings.get();
    const restrictedEnvironments = global.getRestrictedEnvironments();

    if (
      restrictedEnvironments.length > 0 &&
      !restrictedEnvironments.includes(settings.sharedoApi)
    ) {
      try {
        auth.logout();
      } catch {}

      sharedoSettings.clear();
      sharedoCapabilities.clear();
      this.$store.commit("disconnected");

      this.logInfo(
        "User has attempted to connect to an unauthorized Sharedo instance and has been logged out."
      );

      this.$router.push({ path: "/", query: { nologon: true } });
    }
  }

  if (
    this.connected &&
    !this.loggedIn &&
    !tokenStorage.getRefreshToken() &&
    this.$route.query.nologon !== "true"
  ) {
    this.login();
  }

  if (this.connected && !this.loggedIn && !!tokenStorage.getRefreshToken()) {
    auth.renewTokens().then(() => this.$store.commit("loggedIn"));
    this.logInfo("Access tokens renewed.");
  }

  if (this.connected && this.loggedIn) {
    this.logInfo("User is already logged in.  Redirecting to home page.");
    this.$router.push("/");
  }
};

const login = async function () {
  this.logDebug("Starting login.");
  const vm = this;

  let capabilities = sharedoCapabilities.get();
  if (Object.keys(capabilities).length === 0) {
    try {
      capabilities = await sharedoDiscovery.getCapabilities();
      sharedoCapabilities.set(capabilities);
    } catch (e) {
      this.logError("Failed to fetch capabilities before login", e);
    }
  }

  let dialog;
  if (Office.context.ui && typeof Office.context.ui.displayDialogAsync === 'function') {
    const settings = sharedoSettings.get();
    const q = `?q=${encodeURIComponent(encryption.encrypt(settings))}`;
    Office.context.ui.displayDialogAsync(
      // There is currently a bug where Office will open the dialog with a different browser profile
      // and therefore cannot share local storage with the main app.
      // To get around this we pass the Sharedo settings as an encrypted query string.
      `${baseUrl.get()}/#/login${q}`,
      // NOTE:  These dimensions are percentages of the screen size.
      { width: 30, height: 75, displayInIframe: true },
      function (asyncResult) {
        dialog = asyncResult.value;
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          function (arg) {
            vm.logDebug("Returned from ID server dialog.");
            const result = JSON.parse(arg.message);

            tokenStorage.setAccessToken(result.accessToken);
            tokenStorage.setRefreshToken(result.refreshToken);
            vm.logInfo("Access tokens set.");

            vm.$store.commit("loggedIn");
            dialog.close();
            vm.logInfo("User has successfully logged in.");
          }
        );
      }
    );
  } else {
    vm.$router.push(`/login`);
  }
};

export default {
  components: { Feature, Connect, Login, IeWarning },
  data: function () {
    return {
      showConnect: false,
    };
  },
  mounted: initialize,
  beforeUpdate: initialize,
  computed: {
    ...mapState({
      loggedIn: (state) => state.loggedIn,
      connected: (state) => state.connected,
    }),
    isLegacyBrowser: function () {
      return isLegacyBrowser();
    },
    appSettings: function () {
      return global.getAppSettings();
    },
  },
  methods: {
    login,
  },
};
</script>
